import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { AuthGate } from "@/components/AuthGate";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getReview, rerunReview, processRerun, generateReview } from "@/lib/reviews.functions";
import { useIsAdmin } from "@/hooks/use-is-admin";
const getSupabase = () => import("@/integrations/supabase/client").then((m) => m.supabase);
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ProcessingIndicator } from "@/components/ProcessingIndicator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CheckCircle2, AlertTriangle, ShieldAlert, BookOpen, HelpCircle, Sparkles, ListChecks, Info, ChevronDown, ChevronUp } from "lucide-react";
import FullDocumentCritique from "@/components/FullDocumentCritique";
import { ReviewFloatingNav } from "@/components/ReviewFloatingNav";
import { PostMarkingComparison } from "@/components/PostMarkingComparison";
import { MarkerFeedbackForm } from "@/components/MarkerFeedbackForm";
import { PageContainer } from "@/components/PageContainer";
import { Eyebrow, DisplayHeading } from "@/components/editorial";

// Best-effort client telemetry; failures must not affect the UI.
function logReviewClientEvent(
  eventType:
    | "review_auto_retry"
    | "review_manual_retry"
    | "review_stalled_shown",
  metadata: Record<string, unknown>,
) {
  void getSupabase().then((supabase) =>
    supabase
      .rpc("log_usage_event", {
        p_event_type: eventType,
        p_metadata: metadata as never,
      })
      .then(() => {}, () => {}),
  );
}


export const Route = createFileRoute("/reviews/$reviewId")({
  head: () => ({
    meta: [
      { title: "Review — Tiger's Eye" },
      { name: "description", content: "An individual Tiger's Eye academic draft review." },
      { name: "robots", content: "noindex,follow" },
    ],
  }),
  component: () => (<AuthGate><ReviewDetail /></AuthGate>),
});

type ReviewResult = {
  ghostwriting_refusal?: string;
  overall_judgement?: string;
  what_works_well?: string[];
  main_risks_or_weaknesses?: string[];
  category_critique?: { category: string; comments: string }[];
  socratic_questions?: string[];
  errors_and_omissions?: string[];
  referencing_and_bibliography_issues?: { issue: string; evidence: string; fix: string }[];
  academic_integrity_risks?: { risk: string; why: string; suggested_action: string }[];
  priority_revision_plan?: {
    must_fix_before_submission?: string[];
    should_fix_to_improve_quality?: string[];
    could_improve_polish?: string[];
  };
  highest_impact_next_steps?: string[];
  assumptions_made?: string[];
  post_marking?: Record<string, unknown>;
};

function ReviewDetail() {
  const { reviewId } = Route.useParams();
  const fetchFn = useServerFn(getReview);
  const rerunFn = useServerFn(rerunReview);
  const processRerunFn = useServerFn(processRerun);
  const generateFn = useServerFn(generateReview);
  const navigate = useNavigate();
  const { isAdmin } = useIsAdmin();
  const [openIds, setOpenIds] = useState<string[]>([]);
  const openSection = (id: string) => {
    setOpenIds([id]);
    requestAnimationFrame(() => {
      const el = document.getElementById(`section-${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const expandAll = () => setOpenIds(["works","risks","plan","category","errors","refs","integrity","socratic","next-steps","assumptions"]);
  const collapseAll = () => setOpenIds([]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["review", reviewId],
    queryFn: () => fetchFn({ data: { id: reviewId } }),
    refetchInterval: (q) => {
      const status = (q.state.data?.review as { status?: string } | undefined)?.status;
      return status === "pending" || status === "streaming" ? 2500 : false;
    },
  });

  // Auto-kick generation if the row is stuck pending (e.g. the fire-and-forget
  // call from /reviews/new never started, or the user landed here from a link).
  // Also auto-retry once if a "streaming" row has gone silent for >150s — that
  // usually means the worker died mid-call and the row will never flip itself.
  const autoKickedRef = useRef(false);
  const autoRetriedRef = useRef(false);
  const reviewForKick = data?.review as
    | { id: string; status: string; updated_at: string }
    | undefined;
  useEffect(() => {
    if (!reviewForKick) return;
    if (reviewForKick.status === "pending" && !autoKickedRef.current) {
      autoKickedRef.current = true;
      generateFn({ data: { id: reviewForKick.id } }).catch(() => {
        /* swallow — status poll is source of truth */
      });
      return;
    }
    if (reviewForKick.status === "streaming" && !autoRetriedRef.current) {
      const ageMs = Date.now() - new Date(reviewForKick.updated_at).getTime();
      if (ageMs > 360_000) {
        autoRetriedRef.current = true;
        logReviewClientEvent("review_auto_retry", {
          review_id: reviewForKick.id,
          prev_status: reviewForKick.status,
          age_ms: ageMs,
          threshold_ms: 360_000,
        });
        generateFn({ data: { id: reviewForKick.id } }).catch(() => {
          /* swallow */
        });
      }
    }
  }, [reviewForKick, generateFn]);

  const retryGen = useMutation({
    mutationFn: () => {
      const ageMs = reviewForKick
        ? Date.now() - new Date(reviewForKick.updated_at).getTime()
        : null;
      logReviewClientEvent("review_manual_retry", {
        review_id: reviewId,
        prev_status: reviewForKick?.status ?? null,
        age_ms: ageMs,
      });
      return generateFn({ data: { id: reviewId } });
    },
    onSuccess: () => {
      toast.success("Restarted — should be back in a minute.");
      refetch();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rerun = useMutation({
    mutationFn: () => rerunFn({ data: { id: reviewId } }),
    onSuccess: ({ id }) => {
      toast.success("Re-run started");
      processRerunFn({ data: { id } }).catch(() => {
        /* swallow — 504 expected for long runs; status poll picks it up */
      });
      navigate({ to: "/reviews/$reviewId", params: { reviewId: id } });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Log "stalled" UI appearance once per review id so we can analyse 5min+ runs.
  const stalledLoggedRef = useRef<string | null>(null);
  useEffect(() => {
    const r = data?.review as
      | { id: string; status: string; updated_at: string; created_at: string }
      | undefined;
    if (!r) return;
    if (r.status !== "pending" && r.status !== "streaming") return;
    const ageMs = Date.now() - new Date(r.updated_at ?? r.created_at).getTime();
    if (ageMs > 300_000 && stalledLoggedRef.current !== r.id) {
      stalledLoggedRef.current = r.id;
      logReviewClientEvent("review_stalled_shown", {
        review_id: r.id,
        status: r.status,
        age_ms: ageMs,
        threshold_ms: 300_000,
      });
    }
  }, [data]);


  if (isLoading) return <PageContainer className="text-ink-tertiary">Loading review…</PageContainer>;
  if (!data) return <PageContainer>Not found.</PageContainer>;


  const review = data.review as {
    id: string; status: string; intake: Record<string, string>;
    result: ReviewResult | null; tokens_used: number | null;
    created_at: string; updated_at: string; draft_text: string | null;
  };
  const intake = review.intake ?? {};
  const result = review.result ?? {};
  const title = intake.module || intake.course || intake.assessment_type || "Untitled review";

  const ageMs = Date.now() - new Date(review.updated_at ?? review.created_at).getTime();
  const stalled = (review.status === "pending" || review.status === "streaming") && ageMs > 300_000;

  if (review.status === "pending" || review.status === "streaming") {
    return (
      <PageContainer>
        <LayoutSwitcher reviewId={reviewId} />
        <Eyebrow className="mb-3 text-center">{stalled ? "Taking longer than usual" : "Working"}</Eyebrow>
        <DisplayHeading level={1} className="text-center">
          {stalled ? "This review has stalled" : "Tiger's Eye is reading your draft…"}
        </DisplayHeading>
        <p className="text-ink-secondary mt-4 text-center max-w-xl mx-auto">
          {stalled
            ? "The analysis didn't finish in time. This usually means the AI request was cut off. You can restart it below — your draft and settings are saved."
            : "Most reviews complete within 2–4 minutes. The page will refresh automatically."}
        </p>
        {!stalled && (
          <div className="mt-10">
            <ProcessingIndicator
              estimatedSeconds={180}
              stages={[
                "Parsing your draft…",
                "Retrieving relevant academic norms…",
                "Checking referencing and integrity signals…",
                "Drafting critique segments…",
                "Composing the editorial review…",
                "Finalising…",
              ]}
            />
          </div>
        )}
        <div className="mt-8 flex justify-center gap-3">
          {stalled ? (
            <>
              <Button onClick={() => retryGen.mutate()} disabled={retryGen.isPending}>
                {retryGen.isPending ? "Restarting…" : "Restart review"}
              </Button>
              <Link to="/reviews"><Button variant="outline">Back to reviews</Button></Link>
            </>
          ) : (
            <Button variant="outline" onClick={() => refetch()}>Refresh now</Button>
          )}
        </div>
      </PageContainer>
    );
  }


  if (review.status === "failed") {
    return (
      <PageContainer>
        <LayoutSwitcher reviewId={reviewId} />
        <Eyebrow className="mb-3 text-destructive">Failed</Eyebrow>
        <DisplayHeading level={1} className="mb-4">Review didn't generate</DisplayHeading>
        <pre className="text-sm text-ink-secondary whitespace-pre-wrap bg-paper p-4 border border-border">
          {(result as { error?: string }).error ?? "Unknown error"}
        </pre>
        <Link to="/reviews" className="inline-block mt-6"><Button variant="outline">Back to reviews</Button></Link>
      </PageContainer>
    );
  }

  const counts = {
    strengths: result.what_works_well?.length ?? 0,
    risks: result.main_risks_or_weaknesses?.length ?? 0,
    integrity: result.academic_integrity_risks?.length ?? 0,
    referencing: result.referencing_and_bibliography_issues?.length ?? 0,
    mustFix: result.priority_revision_plan?.must_fix_before_submission?.length ?? 0,
  };

  return (
    <PageContainer as="article" className="pb-28 sm:pb-12 animate-fade-in">
      <ReviewFloatingNav onExpandAll={expandAll} onCollapseAll={collapseAll} />

      <LayoutSwitcher reviewId={reviewId} />
      <Eyebrow className="mb-3">Editorial review</Eyebrow>
      <DisplayHeading level={1} className="mb-2">{title}</DisplayHeading>
      <p className="text-sm text-ink-tertiary mb-6">
        {intake.university && `${intake.university} · `}
        {intake.year_of_study && `${intake.year_of_study} · `}
        {new Date(review.created_at).toLocaleString("en-GB")}
      </p>

      {/* Report cover: signal density at a glance. Each chip jumps to + opens its section. */}
      <div className="flex flex-wrap gap-2 mb-10">
        <ToneChip tone="positive" icon={<CheckCircle2 className="h-3.5 w-3.5" />} label={`${counts.strengths} strength${counts.strengths === 1 ? "" : "s"}`} onClick={() => openSection("works")} />
        <ToneChip tone="attention" icon={<AlertTriangle className="h-3.5 w-3.5" />} label={`${counts.risks} risk${counts.risks === 1 ? "" : "s"}`} onClick={() => openSection("risks")} />
        {counts.mustFix > 0 && (
          <ToneChip tone="critical" icon={<ListChecks className="h-3.5 w-3.5" />} label={`${counts.mustFix} must-fix`} onClick={() => openSection("plan")} />
        )}
        {counts.integrity > 0 && (
          <ToneChip tone="critical" icon={<ShieldAlert className="h-3.5 w-3.5" />} label={`${counts.integrity} integrity`} onClick={() => openSection("integrity")} />
        )}
        {counts.referencing > 0 && (
          <ToneChip tone="scholarly" icon={<BookOpen className="h-3.5 w-3.5" />} label={`${counts.referencing} referencing`} onClick={() => openSection("refs")} />
        )}
      </div>

      {result.ghostwriting_refusal && (
        <div className="border-l-2 pl-4 mb-8" style={{ borderColor: "var(--tone-critical)" }}>
          <p className="overline mb-2" style={{ color: "var(--tone-critical)" }}>Refusal</p>
          <p className="text-ink">{result.ghostwriting_refusal}</p>
        </div>
      )}

      {result.overall_judgement && (
        <section className="mb-10">
          <h2 className="overline mb-3">Overall judgement</h2>
          <p className="drop-cap font-serif text-xl leading-relaxed text-ink">{result.overall_judgement}</p>
        </section>
      )}




      <Accordion
        type="multiple"
        value={openIds}
        onValueChange={setOpenIds}
        className="border-t border-border"
      >

        <ToneAccordionList id="works" tone="positive" icon={<CheckCircle2 className="h-4 w-4" />} title="What works well" items={result.what_works_well} />
        <ToneAccordionList id="risks" tone="attention" icon={<AlertTriangle className="h-4 w-4" />} title="Main risks or weaknesses" items={result.main_risks_or_weaknesses} />

        {result.priority_revision_plan && (counts.mustFix + (result.priority_revision_plan.should_fix_to_improve_quality?.length ?? 0) + (result.priority_revision_plan.could_improve_polish?.length ?? 0)) > 0 && (
          <ToneAccordionItem id="plan" tone="critical" icon={<ListChecks className="h-4 w-4" />} title="Priority revision plan" count={counts.mustFix + (result.priority_revision_plan.should_fix_to_improve_quality?.length ?? 0) + (result.priority_revision_plan.could_improve_polish?.length ?? 0)}>
            <TrafficGroup tone="critical" label="Must fix before submission" items={result.priority_revision_plan.must_fix_before_submission} />
            <TrafficGroup tone="attention" label="Should fix to improve" items={result.priority_revision_plan.should_fix_to_improve_quality} />
            <TrafficGroup tone="scholarly" label="Could improve for polish" items={result.priority_revision_plan.could_improve_polish} />
          </ToneAccordionItem>
        )}

        {result.category_critique && result.category_critique.length > 0 && (
          <ToneAccordionItem id="category" tone="scholarly" icon={<Sparkles className="h-4 w-4" />} title="Category critique" count={result.category_critique.length}>
            <div className="space-y-4">
              {result.category_critique.map((c, i) => (
                <div key={i} className="border-l-2 pl-4" style={{ borderColor: "var(--tone-scholarly)" }}>
                  <p className="font-serif text-lg text-primary">{c.category}</p>
                  <p className="text-ink-secondary mt-1">{c.comments}</p>
                </div>
              ))}
            </div>
          </ToneAccordionItem>
        )}

        <ToneAccordionList id="errors" tone="attention" icon={<AlertTriangle className="h-4 w-4" />} title="Errors and omissions" items={result.errors_and_omissions} />

        {result.referencing_and_bibliography_issues && result.referencing_and_bibliography_issues.length > 0 && (
          <ToneAccordionItem id="refs" tone="scholarly" icon={<BookOpen className="h-4 w-4" />} title="Referencing & bibliography" count={result.referencing_and_bibliography_issues.length}>
            <div className="space-y-3">
              {result.referencing_and_bibliography_issues.map((r, i) => (
                <div key={i} className="border border-border bg-card p-4">
                  <p className="font-medium text-primary">{r.issue}</p>
                  {r.evidence && <p className="text-sm text-ink-tertiary mt-1"><em>Evidence:</em> {r.evidence}</p>}
                  {r.fix && <p className="text-sm text-ink-secondary mt-1"><em>Fix:</em> {r.fix}</p>}
                </div>
              ))}
            </div>
          </ToneAccordionItem>
        )}

        {result.academic_integrity_risks && result.academic_integrity_risks.length > 0 && (
          <ToneAccordionItem id="integrity" tone="critical" icon={<ShieldAlert className="h-4 w-4" />} title="Academic integrity risks" count={result.academic_integrity_risks.length}>
            <div className="space-y-3">
              {result.academic_integrity_risks.map((r, i) => (
                <div key={i} className="border-l-2 pl-4" style={{ borderColor: "var(--tone-critical)" }}>
                  <p className="font-medium text-ink">{r.risk}</p>
                  {r.why && <p className="text-sm text-ink-secondary mt-1">{r.why}</p>}
                  {r.suggested_action && <p className="text-sm text-ink-tertiary mt-1"><em>Suggested action:</em> {r.suggested_action}</p>}
                </div>
              ))}
            </div>
          </ToneAccordionItem>
        )}

        <ToneAccordionList id="socratic" tone="scholarly" icon={<HelpCircle className="h-4 w-4" />} title="Socratic questions" items={result.socratic_questions} />
        <ToneAccordionList id="next-steps" tone="positive" icon={<Sparkles className="h-4 w-4" />} title="Highest-impact next steps" items={result.highest_impact_next_steps} />
        <ToneAccordionList id="assumptions" tone="scholarly" icon={<Info className="h-4 w-4" />} title="Assumptions made" items={result.assumptions_made} />
      </Accordion>

      <div className="border-t border-border pt-6 mt-12 flex flex-wrap items-center justify-between gap-3 text-sm text-ink-tertiary">
        <span>{review.tokens_used ? `${review.tokens_used} tokens used` : ""}</span>
        <div className="flex gap-2">
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => rerun.mutate()}
              disabled={rerun.isPending}
              title="Admin: clone this review and regenerate with the current prompts/model"
            >
              {rerun.isPending ? "Starting re-run…" : "Re-run (admin)"}
            </Button>
          )}
          <Link to="/reviews"><Button variant="outline" size="sm">Back to reviews</Button></Link>
        </div>
      </div>

      <div id="results" className="scroll-mt-24">
        <PostMarkingComparison reviewId={review.id} existing={(result.post_marking as never) ?? null} />

        <MarkerFeedbackForm reviewId={review.id} />
      </div>

      {isAdmin && review.draft_text && (
        <AdminSegmentedPreview reviewId={review.id} draftText={review.draft_text} />
      )}
    </PageContainer>
  );
}

type Tone = "positive" | "attention" | "critical" | "scholarly";

function toneColor(tone: Tone) {
  return `var(--tone-${tone})`;
}

function ToneChip({ tone, icon, label, onClick }: { tone: Tone; icon: React.ReactNode; label: string; onClick?: () => void }) {
  const className = "inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border";
  const style = { color: toneColor(tone), borderColor: toneColor(tone), backgroundColor: `color-mix(in oklch, ${toneColor(tone)} 8%, transparent)` };
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${className} cursor-pointer transition hover:scale-[1.03]`} style={style}>
        {icon}{label}
      </button>
    );
  }
  return (
    <span className={className} style={style}>
      {icon}{label}
    </span>
  );
}

function ToneAccordionItem({ id, tone, icon, title, count, children }: { id: string; tone: Tone; icon: React.ReactNode; title: string; count?: number; children: React.ReactNode }) {
  return (
    <AccordionItem id={`section-${id}`} value={id} className="border-b border-border scroll-mt-24" style={{ borderLeft: `2px solid ${toneColor(tone)}`, paddingLeft: "1rem" }}>
      <AccordionTrigger className="hover:no-underline">
        <div className="flex items-center gap-3 text-left">
          <span style={{ color: toneColor(tone) }}>{icon}</span>
          <span className="font-serif text-lg text-primary">{title}</span>
          {typeof count === "number" && count > 0 && (
            <span className="text-xs font-mono px-1.5 py-0.5 rounded" style={{ color: toneColor(tone), backgroundColor: `color-mix(in oklch, ${toneColor(tone)} 10%, transparent)` }}>{count}</span>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="pt-2 pb-6">{children}</AccordionContent>
    </AccordionItem>
  );
}

function ToneAccordionList({ id, tone, icon, title, items }: { id: string; tone: Tone; icon: React.ReactNode; title: string; items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <ToneAccordionItem id={id} tone={tone} icon={icon} title={title} count={items.length}>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex gap-3 text-ink-secondary">
            <span className="font-mono text-xs mt-1" style={{ color: toneColor(tone) }}>▸</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </ToneAccordionItem>
  );
}

function TrafficGroup({ tone, label, items }: { tone: Tone; label: string; items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: toneColor(tone) }} />
        <p className="font-medium" style={{ color: toneColor(tone) }}>{label}</p>
      </div>
      <ul className="space-y-1 pl-4">
        {items.map((it, i) => <li key={i} className="text-ink-secondary list-disc">{it}</li>)}
      </ul>
    </div>
  );
}

function AdminSegmentedPreview({ reviewId, draftText }: { reviewId: string; draftText: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-12 border-t border-dashed border-border pt-6">
      <button
        onClick={() => setOpen((v) => !v)}
        className="text-xs uppercase tracking-wider text-ink-tertiary hover:text-primary"
      >
        {open ? "Hide" : "Show"} segmented analysis (admin preview)
      </button>
      {open && (
        <div className="mt-6">
          <FullDocumentCritique reviewId={reviewId} initialText={draftText} />
        </div>
      )}
    </div>
  );
}

function LayoutSwitcher({ reviewId }: { reviewId: string }) {
  return (
    <div
      role="tablist"
      aria-label="Review layout"
      className="inline-flex rounded-md border border-border bg-secondary p-1 gap-1 mb-6"
    >
      <span
        role="tab"
        aria-selected="true"
        className="px-3 py-1.5 text-sm font-medium rounded-sm bg-primary text-primary-foreground shadow-sm"
      >
        Classic
      </span>
      <Link
        to="/reviews/$reviewId/hybrid"
        params={{ reviewId }}
        role="tab"
        aria-selected="false"
        className="px-3 py-1.5 text-sm font-medium rounded-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        Hybrid (new)
      </Link>
    </div>
  );
}
